/* ================================================================
   calculatorService.ts — talks to the JurisTab ROI Calculator backend
   (juristab-backend-integration/roi-calculator) through the same-origin /api/proxy.

   The proxy (nginx in prod, setupProxy.js in dev) forwards to
   https://api.juristab.com and injects the API-Gateway x-api-key
   server-side, so the key never reaches the browser — same pattern as
   the auth flow (see services/api.ts).
   ================================================================ */

import { apiGet, apiPost, ApiError } from './api';

/** Base path of the calculator service on the custom domain. */
const CALC_BASE = 'calculator';

export type Profession = 'in-house' | 'private-practice' | 'conveyancer';

/** The three product modules. Benefit is summed per module, not blended. */
export type ModuleKey = 'case_management' | 'workflow' | 'legal_research';

/** Plan codes from the live billing catalogue. */
export type PlanCode =
  | 'pay_as_you_go'
  | 'pro_case_management'
  | 'pro_research'
  | 'pro_workflow'
  | 'pro_plus';

export interface ProjectionYear {
  year: number;
  roi_multiple: number;
  subscription_cost: number;
  usage_cost: number;
  cost_of_juristab: number;
  potential_additional_profit: number;
  time_regained_hours: number;
  [key: string]: number;
}

/** One row of the "which plan is cheapest for you" comparison. */
export interface PlanComparisonRow {
  plan_code: PlanCode;
  plan_name: string;
  subscription_cost: number;
  usage_cost: number;
  total_cost: number;
}

export interface CalculatorAssumptions {
  billing_plan?: PlanCode;
  billing_plan_name?: string;
  billing_plan_auto_selected?: boolean;
  billing_cycle?: 'monthly' | 'annual';
  plan_comparison?: PlanComparisonRow[];
  module_hours_steady_state?: Record<ModuleKey, number>;
  effective_time_saved_share?: number;
  capacity_conversion_rate?: number;
  avg_credits_per_page?: number;
  monthly_usage_per_professional?: Record<string, number>;
  [key: string]: unknown;
}

export interface CalculatorReport {
  profession: string;
  currency: string;
  inputs: Record<string, number | string>;
  headline: Record<string, number | string | null>;
  /** Where the firm stands today, before JurisTab. Keyed the same way as a
   *  projection year so the UI can render it as one more column. */
  baseline?: Record<string, number>;
  projection: ProjectionYear[];
  assumptions: CalculatorAssumptions;
  glossary: Record<string, string>;
  /** Present only when the report is shown in a currency other than the published one. */
  fx_note?: string | null;
  disclaimer: string;
}

/** A plan as published by the backend's /plans catalogue. */
export interface PlanCatalogueEntry {
  display_name: string;
  monthly_price: number;
  annual_price: number;
  page_allowance: number | null;
  overage_per_page: number | null;
  rollover_percent: number;
  research_unlimited: boolean;
  case_management_unlimited: boolean;
  sort_order: number;
}

export interface PlanCatalogue {
  default_billing_cycle: 'monthly' | 'annual';
  base_currency: string;
  plans: Record<PlanCode, PlanCatalogueEntry>;
  rate_card: Record<string, unknown>;
  credits: Record<string, unknown>;
  modules: Record<ModuleKey, { label: string; capabilities: number }>;
  total_capabilities: number;
}

const ENDPOINTS: Record<Profession, string> = {
  'in-house': `${CALC_BASE}/calculate/in-house`,
  'private-practice': `${CALC_BASE}/calculate/private-practice`,
  conveyancer: `${CALC_BASE}/calculate/conveyancer`,
};

/** Run an ROI calculation for the given profession. */
/** One row of a team breakdown, as the backend's `team` field takes it. */
export interface TeamMemberInput {
  role: string | null;
  count: number;
  fte: number;
  rate: number | null;
}

export type CalculatorPayload = Record<
  string,
  number | string | string[] | TeamMemberInput[] | null
>;

export async function calculate(
  profession: Profession,
  payload: CalculatorPayload
): Promise<CalculatorReport> {
  try {
    const { data } = await apiPost(ENDPOINTS[profession], payload);
    return data as CalculatorReport;
  } catch (err) {
    if (err instanceof ApiError) {
      const detail = err.data?.detail;
      throw new Error(
        typeof detail === 'string'
          ? detail
          : 'We could not run the calculation. Please check your inputs and try again.'
      );
    }
    throw err;
  }
}

/**
 * Supported currencies (for the currency dropdown).
 *
 * JurisTab is priced in the market's currency (`base`); the values are the
 * indicative FX rate applied to the published price, not a local price list.
 */
export async function getCurrencies(): Promise<{
  default: string;
  base: string;
  currencies: Record<string, number>;
  note?: string;
}> {
  const { data } = await apiGet(`${CALC_BASE}/currencies`);
  return data;
}

/** The published plan catalogue and credit rate card the calculator costs against. */
export async function getPlans(): Promise<PlanCatalogue> {
  const { data } = await apiGet(`${CALC_BASE}/plans`);
  return data as PlanCatalogue;
}
